import * as UserRegisteredEvent from '@ori-events/user-registered';
import { Typography, styled } from '@ori-ui/mui';
import { Placement } from '@ori/analytics-data';
import {
  useApiClient,
  updateCustomerRegistration,
  updateCustomerRegistrationLegalDetails,
  getCustomerRegistrationAnalytics,
  HttpStatusCodes,
} from '@ori/global-api-client-react';
import type {
  AxiosError,
  GlobalCustomerRegistrationsAnalyticsDto,
  GlobalCustomerRegistrationsRegistrationLegalDetailsDto,
} from '@ori/global-api-client-react';
import type { FC } from 'react';
import { useState, useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import {
  useConfiguration,
  useFormError,
  useNotificationData,
  useRegistrationStatus,
  useVirtualPageAnalyticsData,
} from '../../contexts';
import type { IErrors } from '../../interfaces';
import { MessageType } from '../../interfaces';
import { FetchResult, TranslationKey } from '../../models';
import { useLegalData, useLegalMetadata } from '../../models/api';
import { applyNegative, scrollTopFunctionHelper, virtualPageEventDispatch } from '../../utils';
import { BasicSkeleton } from '../BasicSkeleton';
import { LegalDetailsForm } from '../Forms';
import { StyledTitle } from './StepsStyledComponents';

const RootContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
}));

const ContentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
}));

const handleUserRegisteredEvent = (analyticsData: GlobalCustomerRegistrationsAnalyticsDto) => {
  const { consultantNumber, customerTypeId, registrationChannel, sponsorNumber, visitorSegment } = analyticsData;

  // workaround: date object contains client's timezone althrougt API returns only date portion
  const signUpDate = analyticsData.signUpDate ?? new Date();
  const signUpDateOffset = signUpDate.getTimezoneOffset() * 60000;
  const signUpDateWithoutOffset = new Date(signUpDate.getTime() - signUpDateOffset);
  const registerEventData: UserRegisteredEvent.Data = {
    registeredUserId: consultantNumber.toString(),
    customerTypeId: customerTypeId.toString(),
    registrationChannel: registrationChannel ?? '',
    registrationType: 'Self',
    sponsorNumber: sponsorNumber?.toString() ?? '',
    signUpDate: signUpDateWithoutOffset.toISOString(),
    visitorSegment: visitorSegment.toString(),
    placement: Placement.JoinUs,
  };
  window.dispatchEvent(
    new CustomEvent<UserRegisteredEvent.Data>(UserRegisteredEvent.Name, {
      detail: registerEventData,
    }),
  );
};

export const LegalStep: FC = () => {
  const {
    context: { notificationData },
  } = useNotificationData();
  const { setNotificationData } = notificationData;

  const { setFinished } = useRegistrationStatus();

  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { registrationId } = useConfiguration();

  const { data: legalConfig } = useLegalMetadata();

  const isNegativeApprovals = Boolean(legalConfig?.approvals?.shared?.displayAsNegative);

  // initial data
  const { data: legalInitialData } = useLegalData(isNegativeApprovals);

  const {
    context: { errorData },
  } = useFormError();
  const { setErrorData, customErrors } = errorData;

  const api = useApiClient();

  const {
    virtualPageAnalyticsContext: { virtualPageAnalyticsData },
  } = useVirtualPageAnalyticsData();
  const { setPreviousStep, previousStep } = virtualPageAnalyticsData;

  const getCorrectSubmitData = (data: GlobalCustomerRegistrationsRegistrationLegalDetailsDto) => {
    let correctData = data;

    const emptyIDCard = {
      documentsCollection: [],
    };

    if (!data.identityCard?.documentsCollection) {
      correctData = { ...data, identityCard: { ...data.identityCard, ...emptyIDCard } };
    }

    if (!data.additionalIdentityCard?.documentsCollection) {
      correctData = { ...correctData, additionalIdentityCard: emptyIDCard };
    }

    return correctData;
  };

  const getAnalyticsData = async (): Promise<GlobalCustomerRegistrationsAnalyticsDto | undefined> => {
    try {
      const response = await getCustomerRegistrationAnalytics(api, registrationId);
      const result = new FetchResult<GlobalCustomerRegistrationsAnalyticsDto>(response.status);
      if (result.isSuccess()) {
        return response.data;
      }
    } catch (error: unknown) {
      setNotificationData({
        isOpen: true,
        message: (
          <Localization
            id={TranslationKey.cannotGetData}
            defaultMessage={defaultTranslations.cannotGetData}
          />
        ),
        type: MessageType.error,
      });
    }

    return undefined;
  };

  useEffect(() => {
    scrollTopFunctionHelper();
  }, []);

  useEffect(() => {
    virtualPageEventDispatch('LegalInformationStep', previousStep, registrationId);
    setPreviousStep('LegalInformationStep');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- virtual page event should be fired exacly once, no need to reevaluate
  }, []);

  const handleUpdateRegistration = async () => {
    try {
      const updateResponse = await updateCustomerRegistration(api, registrationId, {
        loginType: 'Email',
        registrationId,
        currentStep: 'SubmitStep',
      });
      const result = new FetchResult<unknown>(updateResponse.status);

      if (result.isSuccess()) {
        setSubmitSuccess(true);
        setFinished(true);
        const analyticsData = await getAnalyticsData();
        if (analyticsData !== undefined) {
          handleUserRegisteredEvent(analyticsData);
        }
        if (customErrors && customErrors.length > 0) {
          setErrorData([]);
        }
      }
    } catch (error: unknown) {
      const errorResponse = error as AxiosError;
      if (errorResponse.response?.status === HttpStatusCodes.status400BadRequest) {
        const { Errors } = errorResponse.response.data as IErrors;
        const submitErrors = Errors?.map((er) => ({
          fieldName: er.PropertyName,
          errorCode: er.Code,
        }));
        if (submitErrors?.length && submitErrors.length > 0) {
          setErrorData(submitErrors);
        } else {
          setErrorData([]);
        }
      } else {
        setNotificationData({
          isOpen: true,
          message: (
            <Localization
              id={TranslationKey.cannotUpdate}
              defaultMessage={defaultTranslations.cannotUpdate}
            />
          ),
          type: MessageType.error,
        });
      }
    }
  };

  const onSubmit: SubmitHandler<GlobalCustomerRegistrationsRegistrationLegalDetailsDto> = async (data) => {
    const submitData = getCorrectSubmitData(data);
    const currentSubmitData: GlobalCustomerRegistrationsRegistrationLegalDetailsDto = {
      ...submitData,
      approvals: submitData.approvals && applyNegative(submitData.approvals, isNegativeApprovals),
    };
    try {
      const updateResponseLegal = await updateCustomerRegistrationLegalDetails(api, registrationId, currentSubmitData);
      const result = new FetchResult<unknown>(updateResponseLegal.status);

      if (result.isSuccess()) {
        await handleUpdateRegistration();
      }
    } catch (error: unknown) {
      const errorResponse = error as AxiosError;
      if (errorResponse.response?.status === HttpStatusCodes.status400BadRequest) {
        const { Errors } = errorResponse.response.data as IErrors;
        const submitErrors = Errors?.map((er) => ({
          fieldName: er.PropertyName,
          errorCode: er.Code,
        }));
        if (submitErrors?.length && submitErrors.length > 0) {
          setErrorData(submitErrors);
        } else {
          setErrorData([]);
        }
      } else {
        setNotificationData({
          isOpen: true,
          message: (
            <Localization
              id={TranslationKey.cannotUpdate}
              defaultMessage={defaultTranslations.cannotUpdate}
            />
          ),
          type: MessageType.error,
        });
      }
    }
  };

  if (!legalInitialData) {
    return <BasicSkeleton />;
  }

  return (
    <RootContainer>
      <ContentWrapper>
        <StyledTitle
          variant="h4"
          color="textPrimary"
        >
          <Localization
            id={TranslationKey.ThirdStep_Header}
            defaultMessage={defaultTranslations.ThirdStep_Header}
          />
        </StyledTitle>
        <Typography
          variant="subtitle1"
          color="textPrimary"
          textAlign="center"
        >
          <Localization
            id={TranslationKey.ThirdStep_HeaderText}
            defaultMessage={defaultTranslations.ThirdStep_HeaderText}
          />
        </Typography>

        <LegalDetailsForm
          config={legalConfig}
          initialData={legalInitialData}
          isSuccessSubmit={submitSuccess}
          onSubmitForm={onSubmit}
        />
      </ContentWrapper>
    </RootContainer>
  );
};
